import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    Divider,
    Chip,
    Avatar,
    Button,
    IconButton,
    Alert,
    Snackbar,
    useTheme,
    useMediaQuery,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Tooltip,
    alpha,
    Collapse,
    FormHelperText,
    Slide,
    InputAdornment
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Error as ErrorIcon,
    Print as PrintIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Pending as PendingIcon,
    Person as PersonIcon,
    Money as MoneyIcon,
    FilterList as FilterListIcon,
    Search as SearchIcon,
    Payment as PaymentIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Verified
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import AuthService from '../Auth/AuthService';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CloseIcon from '@mui/icons-material/Close';
import QRCode from "react-qr-code";

// Styled components
const RootContainer = styled(Container)(({ theme }) => ({
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
    minHeight: '100vh'
}));

const GlassPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: 20,
    background: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(20px)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: '0 12px 48px rgba(0, 0, 0, 0.12)'
    }
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
    const getColor = () => {
        switch (status) {
            case 'PAID':
            case 'COMPLETED':
                return '#00b894';
            case 'UNPAID':
                return '#ff7675';
            case 'PENDING':
                return '#fdcb6e';
            case 'PROCESSING':
                return '#0984e3';
            default:
                return theme.palette.grey[500];
        }
    };
    return {
        fontWeight: 600,
        fontSize: '0.75rem',
        height: 28,
        borderRadius: 8,
        backgroundColor: getColor(),
        color: 'white',
        '& .MuiChip-icon': { color: 'white' }
    };
});

const StatsCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2),
    borderRadius: 16,
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.01)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
    }
}));

const MobileCard = styled(Card)(({ theme }) => ({
    borderRadius: 12,
    marginBottom: theme.spacing(2),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)'
    }
}));

// Helper function to get last month
const getLastMonth = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toLocaleString('default', { month: 'long' }).toUpperCase();
};

// Helper function to get last month's year
const getLastMonthYear = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.getFullYear();
};

// Month options
const MONTHS = [
    'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
    'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
];

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Main Component
const SalarySlipView = ({ user }) => {
    const theme = useTheme();
    // const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const hasFetched = useRef(false);
    // State
    const [salarySlips, setSalarySlips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Filter state - Default to last month
    const [selectedMonth, setSelectedMonth] = useState(getLastMonth());
    const [selectedYear, setSelectedYear] = useState(getLastMonthYear());
    const [filterDialog, setFilterDialog] = useState(false);

    // Search
    const [searchQuery, setSearchQuery] = useState('');

    // Pagination & Sorting
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [orderBy, setOrderBy] = useState('id');
    const [order, setOrder] = useState('asc');

    // Edit Dialog
    const [editDialog, setEditDialog] = useState(false);
    // const [selectedSlip, setSelectedSlip] = useState(null);
    const [editingData, setEditingData] = useState(null);

    // Delete Dialog
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Mobile expand
    const [expandedId, setExpandedId] = useState(null);

    //payment
    const [upiLink, setUpiLink] = useState(null);
    const [showQRDialog, setShowQRDialog] = useState(false);
    const [showPostPaymentDialog, setShowPostPaymentDialog] = useState(false);
    // const [paymentStatus, setPaymentStatus] = useState('pending');
    const [salaryAmount, setSalaryAmount] = useState(null);

    // Fetch data
    const loadSalarySlips = async (month, year) => {
        try {
            setLoading(true);
            setError(null);

            const url = AuthService.pathUrl("GET", `/salary/slips/all?month=${month}&year=${year}`);
            const response = await AuthService.getAuth(url);

            setSalarySlips(Array.isArray(response) ? response : []);
        } catch (err) {
            setError(err.message || 'Failed to load salary slips');
            setSnackbar({
                open: true,
                message: 'Failed to load salary slips. Please try again.',
                severity: 'error'
            });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    // Initial load - fetch last month's data
    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;
        loadSalarySlips(selectedMonth, selectedYear);
    }, []);

    // Apply filter
    const handleApplyFilter = () => {
        setFilterDialog(false);
        loadSalarySlips(selectedMonth, selectedYear);
        setPage(0);
    };

    const handleRefresh = () => {
        setRefreshing(true);
        loadSalarySlips(selectedMonth, selectedYear);
    };

    // Sorting
    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Navigate to payment
    const handleMakePayment = (slip) => {

        if (!slip?.upiId === undefined || slip?.upiId === null) {
            setSnackbar({
                open: true,
                message: 'Please update employee UPI ID for a payment.',
                severity: 'warning',
            });
            // return;
        }
        setLoading(true);
        try {
            setSalaryAmount(slip);

            const upiUrl = generateUPIUrl(slip, slip?.id);
            setUpiLink(upiUrl);
            setShowQRDialog(true);

            const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

            if (isMobileDevice) {
                const upiDeepLink = `upi://pay?pa=${slip?.upiId}&pn=${encodeURIComponent(slip.employeeName)}&am=${slip?.netSalary}&cu=INR&tn=${encodeURIComponent(`${"salary payment"} - ${slip?.id}`)}`;
                window.location.href = upiDeepLink;
            }

            setSnackbar({
                open: true,
                message: 'Payment initiated. Please complete the payment in your UPI app.',
                severity: 'success',
            });

        } catch (error) {
            console.error("Payment failed:", error.message);
            setSnackbar({
                open: true,
                message: `Payment failed: ${error.message}`,
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };
    // Generate UPI payment URL
    const generateUPIUrl = (slip, transactionId = null) => {
        const baseUrl = 'upi://pay';
        const params = new URLSearchParams({
            pa: slip?.upiId,
            pn: slip.employeeName,
            am: slip?.netSalary?.toFixed ? slip.netSalary.toFixed(2) : slip?.netSalary,
            cu: 'INR',
            tn: `${"salary Payment"} - ${transactionId || 'TXN'}`,
            mc: slip.MERCHANT_CODE,
        });

        return `${baseUrl}?${params.toString()}`;
    };

    // Handle manual payment confirmation
    const handleManualPaymentConfirmation = () => {
        setShowQRDialog(false);
        setShowPostPaymentDialog(true);
    };

    const handleCloseQRDialog = () => {
        setShowQRDialog(false);
    };
    const handleClosePostPaymentDialog = () => {
        setShowPostPaymentDialog(false);
    };
    const copyUPIId = () => {
        navigator.clipboard.writeText(salaryAmount?.upiId);
        setSnackbar({
            open: true,
            message: 'UPI ID copied to clipboard',
            severity: 'success',
        });
    };

    const postPayment = async () => {

        if (!editingData?.payMode || editingData?.payStatus !== 'PAID') {
            setSnackbar({
                open: true,
                message: 'Please select a pay mode and PAID as the payment status.',
                severity: 'warning',
            });
            return;
        }

        const updateSal = {
            payMode: editingData.payMode,
            payStatus: editingData.payStatus,
            transactionId: editingData.transactionId,
            netSalary: editingData.netSalary,
            id: salaryAmount.id,
        }
        console.log(updateSal);
        try {
            const data = await AuthService.putAuth(
                AuthService.pathUrl("PUT", `/salary/${updateSal?.id}`),
                updateSal
            );
            const updatedList = salarySlips.map(s =>
                s?.id === data?.id ? data : s
            );
            setSalarySlips(updatedList);
            setEditDialog(false);
            setShowPostPaymentDialog(false);
            setSnackbar({
                open: true,
                message: 'Salary slip updated successfully!',
                severity: 'success'
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message: 'Failed to update salary slip. Please try again.',
                severity: 'error'
            });
        }
    };


    // Edit
    const handleEdit = (slip) => {
        // setSelectedSlip(slip);
        setEditingData({ ...slip });
        setEditDialog(true);
    };

    const handleSaveEdit = async () => {
        try {
            await AuthService.putAuth(
                AuthService.pathUrl("PUT", `/salary/${editingData?.id}`),
                editingData
            );
            const updatedList = salarySlips.map(s =>
                s?.id === editingData?.id ? editingData : s
            );
            setSalarySlips(updatedList);
            setEditDialog(false);
            setSnackbar({
                open: true,
                message: 'Salary slip updated successfully!',
                severity: 'success'
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message: 'Failed to update salary slip. Please try again.',
                severity: 'error'
            });
        }
    };

    // Delete
    const handleDelete = (slip) => {
        setDeleteTarget(slip);
        setDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await AuthService.deleteAuth(
                AuthService.pathUrl("DELETE", `/salary-slips/${deleteTarget?.id}`)
            );
            const updatedList = salarySlips.filter(s => s?.id !== deleteTarget?.id);
            setSalarySlips(updatedList);
            setDeleteDialog(false);
            setSnackbar({
                open: true,
                message: 'Salary slip deleted successfully!',
                severity: 'success'
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message: 'Failed to delete salary slip. Please try again.',
                severity: 'error'
            });
        }
    };

    // Formatting helpers
    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null) return '₹0';
        return `₹${Number(amount).toLocaleString('en-IN')}`;
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'PAID':
            case 'COMPLETED':
                return <CheckCircleIcon fontSize="small" />;
            case 'UNPAID':
                return <CancelIcon fontSize="small" />;
            case 'PENDING':
            case 'PROCESSING':
                return <PendingIcon fontSize="small" />;
            default:
                return null;
        }
    };

    const stats = useMemo(() => {
        return salarySlips.reduce(
            (acc, s) => {
                acc.total += 1;
                if (s.payStatus === 'PAID' || s.payStatus === 'COMPLETED') acc.paid += 1;
                else if (s.payStatus === 'PENDING' || s.payStatus === 'PROCESSING') acc.pending += 1;
                else if (s.payStatus === 'UNPAID') acc.unpaid += 1;
                acc.totalNetPay += Number(s.netSalary || 0);
                return acc;
            },
            { total: 0, paid: 0, pending: 0, unpaid: 0, totalNetPay: 0 }
        );
    }, [salarySlips]);

    const filteredData = useMemo(() => {
        if (!searchQuery.trim()) return salarySlips;
        const q = searchQuery.trim().toLowerCase();
        return salarySlips.filter(
            (s) =>
                s.employeeName?.toLowerCase().includes(q) ||
                s.employeeId?.toLowerCase?.().includes(q) ||
                String(s.employeeId || '').includes(q)
        );
    }, [salarySlips, searchQuery]);

    const sortedData = useMemo(() => {
        return [...filteredData].sort((a, b) => {
            const aVal = a[orderBy] || '';
            const bVal = b[orderBy] || '';
            if (aVal < bVal) return order === 'asc' ? -1 : 1;
            if (aVal > bVal) return order === 'asc' ? 1 : -1;
            return 0;
        });
    }, [filteredData, orderBy, order]);

    const paginatedData = useMemo(() => {
        return sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [sortedData, page, rowsPerPage]);

    // Loading skeleton
    if (loading) {
        return (
            <RootContainer maxWidth="xl">
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                    <Skeleton variant="text" width={300} height={40} />
                    <Box display="flex" gap={2}>
                        <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 2 }} />
                        <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 2 }} />
                    </Box>
                </Box>
                <Grid container spacing={3} mb={4}>
                    {[1, 2, 3, 4].map(i => (
                        <Grid item xs={6} md={3} key={i}>
                            <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 3 }} />
                        </Grid>
                    ))}
                </Grid>
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3 }} />
            </RootContainer>
        );
    }

    // Error state
    if (error && !loading) {
        return (
            <RootContainer maxWidth="xl">
                <GlassPaper sx={{ textAlign: 'center', py: 8 }}>
                    <ErrorIcon sx={{ fontSize: 72, color: 'error.main', mb: 2 }} />
                    <Typography variant="h5" color="error" gutterBottom fontWeight="600">
                        Error Loading Salary Slips
                    </Typography>
                    <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                        {error}
                    </Typography>
                    <Button variant="contained" startIcon={<RefreshIcon />} onClick={handleRefresh}>
                        Retry
                    </Button>
                </GlassPaper>
            </RootContainer>
        );
    }

    return (
        <RootContainer maxWidth="xl">
            {/* Empty state */}
            {!salarySlips || salarySlips.length === 0 ? (

                <GlassPaper sx={{ textAlign: 'center', py: 8 }}>
                    <MoneyIcon sx={{ fontSize: 72, color: 'action.disabled', mb: 2 }} />
                    <Typography variant="h5" gutterBottom fontWeight="600">
                        No Salary Slips Found
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                        No salary records available for {selectedMonth} {selectedYear}.
                    </Typography>
                    <Box mt={3}>
                        <Button
                            variant="outlined"
                            startIcon={<FilterListIcon />}
                            onClick={() => setFilterDialog(true)}
                            sx={{ mr: 2 }}
                        >
                            Change Filter
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefresh}
                        >
                            Refresh
                        </Button>
                    </Box>
                </GlassPaper>
            ) : (<>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} flexWrap="wrap" gap={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                        {/* <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                            <ArrowBackIcon />
                        </IconButton> */}
                        <Box>
                            <Typography variant="h4" fontWeight="700" color="primary" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                                Salary Slips
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {selectedMonth} {selectedYear} - {stats.total} record(s) found
                            </Typography>
                        </Box>
                    </Box>

                    {/* On mobile these collapse to icon-only buttons to save horizontal space */}
                    <Box display="flex" gap={1} flexWrap="wrap">
                        {isMobile ? (
                            <>
                                <Tooltip title={`${selectedMonth} ${selectedYear}`}>
                                    <IconButton onClick={() => setFilterDialog(true)} size="small" sx={{ border: 1, borderColor: 'divider' }}>
                                        <FilterListIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Refresh">
                                    <IconButton onClick={handleRefresh} disabled={refreshing} size="small" sx={{ border: 1, borderColor: 'divider' }}>
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Print">
                                    <IconButton onClick={() => window.print()} size="small" sx={{ border: 1, borderColor: 'divider' }}>
                                        <PrintIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                {/* <Tooltip title="Export">
                                    <IconButton size="small" color="primary" sx={{ border: 1, borderColor: 'primary.main' }}>
                                        <DownloadIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip> */}
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="outlined"
                                    startIcon={<FilterListIcon />}
                                    onClick={() => setFilterDialog(true)}
                                >
                                    {selectedMonth} {selectedYear}
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon />}
                                    onClick={handleRefresh}
                                    disabled={refreshing}
                                >
                                    {refreshing ? 'Refreshing...' : 'Refresh'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<PrintIcon />}
                                    onClick={() => window.print()}
                                >
                                    Print
                                </Button>
                                {/* <Button
                                    variant="contained"
                                    startIcon={<DownloadIcon />}
                                >
                                    Export
                                </Button> */}
                            </>
                        )}
                    </Box>
                </Box>

                {/* Stats */}
                <Grid container spacing={3} mb={4}>
                    <Grid item xs={6} md={3}>
                        <StatsCard>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, width: 40, height: 40 }}>
                                    <MoneyIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h5" fontWeight="700">{stats.total}</Typography>
                                    <Typography variant="caption" color="textSecondary">Total Slips</Typography>
                                </Box>
                            </Box>
                        </StatsCard>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatsCard>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Avatar sx={{ bgcolor: alpha('#00b894', 0.1), color: '#00b894', width: 40, height: 40 }}>
                                    <CheckCircleIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h5" fontWeight="700">{stats.paid}</Typography>
                                    <Typography variant="caption" color="textSecondary">Paid</Typography>
                                </Box>
                            </Box>
                        </StatsCard>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatsCard>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Avatar sx={{ bgcolor: alpha('#fdcb6e', 0.1), color: '#fdcb6e', width: 40, height: 40 }}>
                                    <PendingIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h5" fontWeight="700">{stats.pending}</Typography>
                                    <Typography variant="caption" color="textSecondary">Pending</Typography>
                                </Box>
                            </Box>
                        </StatsCard>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatsCard>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Avatar sx={{ bgcolor: alpha('#ff7675', 0.1), color: '#ff7675', width: 40, height: 40 }}>
                                    <CancelIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h5" fontWeight="700">{stats.unpaid}</Typography>
                                    <Typography variant="caption" color="textSecondary">Unpaid</Typography>
                                </Box>
                            </Box>
                        </StatsCard>
                    </Grid>
                </Grid>

                {/* Table / Cards */}
                <GlassPaper sx={{ p: { xs: 1.5, sm: 3 } }}>
                    {isMobile ? (
                        // Mobile Card View
                        <Box>
                            <Typography variant="h6" fontWeight="700" gutterBottom>
                                Salary Slips
                                <Chip label={stats.total} size="small" color="primary" sx={{ ml: 1, fontWeight: 600 }} />
                            </Typography>

                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search by name or ID..."
                                value={searchQuery}
                                onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                                sx={{ mb: 2 }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }
                                }}
                            />

                            <Divider sx={{ mb: 2 }} />
                            {paginatedData.map((slip) => (
                                <MobileCard key={slip?.id}>
                                    <CardContent>
                                        <Box display="flex" alignItems="center" gap={2}>
                                            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                                                <PersonIcon />
                                            </Avatar>
                                            <Box flex={1} minWidth={0}>
                                                <Typography variant="subtitle1" fontWeight="600" noWrap>
                                                    {slip.employeeName || 'N/A'}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {slip.employeeId}
                                                </Typography>
                                            </Box>
                                            <IconButton onClick={() => setExpandedId(expandedId === slip?.id ? null : slip?.id)}>
                                                {expandedId === slip?.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </Box>
                                        <Collapse in={expandedId === slip?.id}>
                                            <Box mt={2} pt={2} borderTop={`1px solid ${alpha(theme.palette.divider, 0.5)}`}>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" color="textSecondary">Designation</Typography>
                                                        <Typography variant="body2">{slip.designation || 'N/A'}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" color="textSecondary">Department</Typography>
                                                        <Typography variant="body2">{slip.department || 'N/A'}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" color="textSecondary">Gross Salary</Typography>
                                                        <Typography variant="body2">{slip.grossSalary || 'N/A'}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" color="textSecondary">Net Pay</Typography>
                                                        <Typography variant="body2" fontWeight="600" color="primary">
                                                            {formatCurrency(slip.netSalary)}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" color="textSecondary">Status</Typography>
                                                        <StatusChip
                                                            label={slip.payStatus || 'PENDING'}
                                                            status={slip.payStatus}
                                                            size="small"
                                                            icon={getStatusIcon(slip.payStatus)}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Box display="flex" justifyContent="flex-end" gap={1} mt={1} flexWrap="wrap">
                                                            {slip.payStatus !== 'PAID' && slip.payStatus !== 'COMPLETED' ? (<>
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    color="success"
                                                                    onClick={() => handleMakePayment(slip)}
                                                                >
                                                                    <PaymentIcon fontSize="small" /> Pay Now
                                                                </Button>
                                                                <Button size="small" variant="outlined" onClick={() => handleEdit(slip)}>
                                                                    <EditIcon fontSize="small" /> Edit
                                                                </Button>
                                                            </>) : (
                                                                <Verified color="secondary" />
                                                            )}
                                                            {user?.roles === "SUPERADMIN" && (
                                                                <Button size="small" variant="outlined" color="error" onClick={() => handleDelete(slip)}>
                                                                    <DeleteIcon fontSize="small" /> Delete
                                                                </Button>
                                                            )}
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Collapse>
                                    </CardContent>
                                </MobileCard>
                            ))}
                            {paginatedData.length === 0 && (
                                <Typography variant="body2" color="textSecondary" textAlign="center" py={4}>
                                    No results match "{searchQuery}".
                                </Typography>
                            )}
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={sortedData.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Box>
                    ) : (
                        // Desktop Table View
                        <>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                                <Typography variant="h6" fontWeight="700">
                                    Salary Slips
                                    <Chip label={stats.total} size="small" color="primary" sx={{ ml: 1, fontWeight: 600 }} />
                                </Typography>
                                <TextField
                                    size="small"
                                    placeholder="Search by name or ID..."
                                    value={searchQuery}
                                    onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                                    sx={{ minWidth: 260 }}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }
                                    }}
                                />
                            </Box>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                                            <TableCell>
                                                <TableSortLabel active={orderBy === 'id'} direction={order} onClick={() => handleSort('id')}>
                                                    <Typography fontWeight="600">ID</Typography>
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel active={orderBy === 'employeeName'} direction={order} onClick={() => handleSort('employeeName')}>
                                                    <Typography fontWeight="600">Employee</Typography>
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <Typography fontWeight="600">Designation</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography fontWeight="600">Department</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography fontWeight="600">Gross Salary</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography fontWeight="600">Deductions</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <TableSortLabel active={orderBy === 'netSalary'} direction={order} onClick={() => handleSort('netSalary')}>
                                                    <Typography fontWeight="600">Net Pay</Typography>
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography fontWeight="600">Status</Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography fontWeight="600">Actions</Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedData.map((slip) => (
                                            <TableRow key={slip?.id} hover>
                                                <TableCell>#{slip?.id}</TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={2}>
                                                        <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                                                            <PersonIcon fontSize="small" />
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="500">{slip.employeeName}</Typography>
                                                            <Typography variant="caption" color="textSecondary">{slip.employeeId}</Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{slip.designation || 'N/A'}</TableCell>
                                                <TableCell>{slip.department || 'N/A'}</TableCell>
                                                <TableCell>{slip.grossSalary || 'N/A'}</TableCell>
                                                <TableCell>{slip.totalDeductions || '0.0'}</TableCell>
                                                <TableCell align="right">
                                                    <Typography fontWeight="600" color="primary">
                                                        {formatCurrency(slip.netSalary)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <StatusChip
                                                        label={slip.payStatus || 'PENDING'}
                                                        status={slip.payStatus}
                                                        size="small"
                                                        icon={getStatusIcon(slip.payStatus)}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Stack direction="row" spacing={1} justifyContent="center">
                                                        {slip.payStatus !== 'PAID' && slip.payStatus !== 'COMPLETED' ? (<>
                                                            <Tooltip title="Make Payment">
                                                                <IconButton
                                                                    size="small"
                                                                    color="success"
                                                                    onClick={() => handleMakePayment(slip)}
                                                                >
                                                                    <PaymentIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Edit">
                                                                <IconButton size="small" color="primary" onClick={() => handleEdit(slip)}>
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>) : (
                                                            <Verified color="secondary" />
                                                        )}
                                                        {user?.roles === "SUPERADMIN" && (
                                                            <Tooltip title="Delete">
                                                                <IconButton size="small" color="error" onClick={() => handleDelete(slip)}>
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>)}
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {paginatedData.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        No results match "{searchQuery}".
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={sortedData.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </>
                    )}
                </GlassPaper>
            </>)}
            {/* Filter Dialog */}
            <Dialog
                open={filterDialog}
                onClose={() => setFilterDialog(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{ sx: { borderRadius: isMobile ? 0 : 4, p: 1 } }}
            >
                <DialogTitle>
                    <Typography variant="h6" component="div" fontWeight="700">Filter Salary Slips</Typography>
                    <Typography variant="caption" color="textSecondary">Select month and year to view</Typography>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Month</InputLabel>
                                <Select
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                    label="Month"
                                >
                                    {MONTHS.map((month) => (
                                        <MenuItem key={month} value={month}>{month}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Year</InputLabel>
                                <Select
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(e.target.value)}
                                    label="Year"
                                >
                                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                                        <MenuItem key={year} value={year}>{year}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setFilterDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleApplyFilter}>Apply Filter</Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog
                open={editDialog}
                onClose={() => setEditDialog(false)}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: { borderRadius: isMobile ? 0 : 4, p: 1 }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="h6" component="div" fontWeight="700">
                        Edit Salary Slip
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Update salary details and status
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Employee upi Id"
                                value={editingData?.upiId || ''}
                                onChange={(e) => setEditingData({ ...editingData, upiId: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                disabled
                                label="Employee Name"
                                value={editingData?.employeeName || ''}
                                onChange={(e) => setEditingData({ ...editingData, employeeName: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Designation"
                                value={editingData?.designation || ''}
                                onChange={(e) => setEditingData({ ...editingData, designation: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Department"
                                value={editingData?.department || ''}
                                onChange={(e) => setEditingData({ ...editingData, department: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Net Pay"
                                type="number"
                                value={editingData?.netSalary || ''}
                                onChange={(e) => setEditingData({ ...editingData, netSalary: parseFloat(e.target.value) })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Pay Status</InputLabel>
                                <Select
                                    value={editingData?.payStatus || 'PENDING'}
                                    onChange={(e) => setEditingData({ ...editingData, payStatus: e.target.value })}
                                    label="Pay Status"
                                >
                                    <MenuItem value="PAID">Paid</MenuItem>
                                    <MenuItem value="UNPAID">Unpaid</MenuItem>
                                    <MenuItem value="PENDING">Pending</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Bank"
                                value={editingData?.bank || ''}
                                onChange={(e) => setEditingData({ ...editingData, bank: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Account No."
                                value={editingData?.accountNo || ''}
                                onChange={(e) => setEditingData({ ...editingData, accountNo: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setEditDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveEdit}>Save Changes</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog
                open={deleteDialog}
                onClose={() => setDeleteDialog(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 4, p: 1 }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="h6" component="div" fontWeight="700" color="error">
                        Delete Salary Slip
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box textAlign="center" py={2}>
                        <DeleteIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                        <Typography variant="body1" gutterBottom>
                            Are you sure you want to delete this salary slip?
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            <strong>{deleteTarget?.employeeName}</strong> - {formatCurrency(deleteTarget?.netSalary)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                            This action cannot be undone.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', p: 2 }}>
                    <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleConfirmDelete}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* QR Code Dialog */}
            <Dialog
                open={showQRDialog}
                onClose={handleCloseQRDialog}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                TransitionComponent={Transition}
            >
                <DialogTitle>
                    Complete Payment via UPI
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseQRDialog}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Amount: ₹{salaryAmount?.netSalary}
                        </Typography>

                        {/* QR Code Display */}
                        {editingData?.upiId !== null && (
                            <Box
                                sx={{
                                    width: { xs: '100%', sm: 250 },
                                    maxWidth: 250,
                                    height: { xs: 'auto', sm: 250 },
                                    aspectRatio: '1 / 1',
                                    margin: '20px auto',
                                    border: '2px solid #e0e0e0',
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'white',
                                    p: 2,
                                }}
                            >
                                {upiLink ? (
                                    <QRCode
                                        value={upiLink}
                                        size={200}
                                        bgColor="#ffffff"
                                        fgColor="#000000"
                                        level="H"
                                        includeMargin={true}
                                        renderAs="canvas" // or 'svg'
                                        style={{ width: '100%', height: '100%', maxWidth: 200, maxHeight: 200 }}
                                    />
                                ) : (
                                    <QrCodeIcon sx={{ fontSize: 80, color: '#999' }} />
                                )}
                            </Box>
                        )}
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Scan this QR code with any UPI app (Google Pay, PhonePe, Paytm, etc.)
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="body2" gutterBottom>
                            <strong>UPI ID:</strong> {salaryAmount?.upiId}
                        </Typography>

                        <Typography variant="body2" gutterBottom>
                            <strong>Order ID:</strong> {salaryAmount?.id || 'N/A'}
                        </Typography>

                        <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={copyUPIId}
                                sx={{ borderRadius: 20 }}
                            >
                                Copy UPI ID
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={handleManualPaymentConfirmation}
                                sx={{ borderRadius: 20 }}
                            >
                                I've Completed Payment
                            </Button>
                            {navigator.userAgent.match(/Android|iPhone|iPad|iPod/i) && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={() => {
                                        if (upiLink) {
                                            window.location.href = upiLink;
                                        }
                                    }}
                                    sx={{ borderRadius: 20 }}
                                >
                                    Open UPI App
                                </Button>
                            )}
                        </Box>

                        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                            After payment, click "I've Completed Payment" to confirm
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Post-Payment Dialog */}
            <Dialog
                open={showPostPaymentDialog}
                onClose={handleClosePostPaymentDialog}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                TransitionComponent={Transition}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <CheckCircleIcon sx={{ fontSize: 54, color: 'success.main' }} />
                    </Box>
                    Payment Successful! 🎉
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Grid container spacing={2} sx={{ mb: 1 }}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    required
                                    label="transaction Id"
                                    value={editingData?.transactionId || ''}
                                    onChange={(e) => setEditingData({ ...editingData, transactionId: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Pay Mode</InputLabel>
                                    <Select
                                        value={editingData?.payMode || ''}
                                        onChange={(e) => setEditingData({ ...editingData, payMode: e.target.value })}
                                        label="Pay Mode"
                                        required
                                    >
                                        <MenuItem value="UPI">UPI</MenuItem>
                                        <MenuItem value="BANK">Bank</MenuItem>
                                        <MenuItem value="CASH">Cash</MenuItem>
                                        <MenuItem value="CHEQUE">Cheque</MenuItem>
                                    </Select>
                                    {!editingData?.payMode && (
                                        <FormHelperText sx={{ color: 'warning.main' }}>
                                            Please select payment mode
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Pay Status</InputLabel>
                                    <Select
                                        value={editingData?.payStatus || ''}
                                        onChange={(e) => setEditingData({ ...editingData, payStatus: e.target.value })}
                                        label="Pay Status"
                                        required
                                    >
                                        <MenuItem value="PAID">Paid</MenuItem>
                                        <MenuItem value="UNPAID">Unpaid</MenuItem>
                                        <MenuItem value="PENDING">Pending</MenuItem>
                                    </Select>
                                    {editingData?.payStatus !== 'PAID' && (
                                        <FormHelperText sx={{ color: 'warning.main' }}>
                                            Please select PAID to complete payment
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center">
                            <Button
                                variant="contained"
                                onClick={postPayment}
                                disabled={!editingData?.payMode || editingData?.payStatus !== 'PAID' || !editingData?.transactionId}
                                sx={{
                                    borderRadius: 30,
                                    py: 0.5,
                                    px: 2,
                                }}
                            >
                                Submit
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleClosePostPaymentDialog}
                                sx={{ borderRadius: 30 }}
                            >
                                Close
                            </Button>
                        </Stack>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </RootContainer>
    );
};

export default SalarySlipView;
